"use client";

import { Howl } from "howler";
import { usePathname } from "next/navigation";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

export type AudioTrackingProps = Record<string, unknown>;

type AudioContextType = {
  play: (id: string, url: string, isLooping?: boolean, trackingProps?: AudioTrackingProps) => void;
  playingId: null | string;
  stop: () => void;
};

const AudioContext = createContext<AudioContextType | null>(null);

/**
 * Use this provider to play audio in child components. The provider handles play/pause and making sure only one audio is playing at a time.
 */
export function AudioProvider({ children }: { children: React.ReactNode }) {
  const [playingId, setPlayingId] = useState<null | string>(null);
  const pathname = usePathname();

  const currentHowlRef = useRef<Howl | null>(null);
  const currentIdRef = useRef<string | null>(null);
  const currentTrackingPropsRef = useRef<AudioTrackingProps | null>(null);
  const isMountedRef = useRef(true);

  const destroyHowl = useCallback(() => {
    const h = currentHowlRef.current;
    if (h) {
      h.stop();
      h.unload();
      currentHowlRef.current = null;
    }
    currentIdRef.current = null;
    currentTrackingPropsRef.current = null;
  }, []);

  const stop = useCallback(() => {
    destroyHowl();
    setPlayingId(null);
  }, [destroyHowl]);

  const play = useCallback(
    (id: string, url: string, isLooping?: boolean, trackingProps?: AudioTrackingProps) => {
      // Build full tracking props with audio_id automatically included
      const fullTrackingProps = trackingProps ? { audio_id: id, ...trackingProps } : null;

      // If clicking the same audio that's playing, pause it
      if (currentIdRef.current === id && currentHowlRef.current) {
        if (currentTrackingPropsRef.current) {
          captureEvent(EventName.mw_audio_paused, currentTrackingPropsRef.current);
        }
        stop();
        return;
      }

      destroyHowl();

      // Use HTML5 audio on mobile to avoid iOS Web Audio API restrictions
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      const howl = new Howl({
        src: [url],
        html5: isMobile, // Use HTML5 on mobile for better iOS compatibility
        loop: !!isLooping,
        onend: () => {
          if (!isLooping && isMountedRef.current && currentIdRef.current === id) {
            if (currentTrackingPropsRef.current) {
              captureEvent(EventName.mw_audio_completed, currentTrackingPropsRef.current);
            }
            setPlayingId(null);
            currentIdRef.current = null;
            currentTrackingPropsRef.current = null;
          }
        },
        onloaderror: (_soundId, error) => {
          if (isMountedRef.current) {
            if (currentTrackingPropsRef.current) {
              captureEvent(EventName.mw_audio_play_error, {
                ...currentTrackingPropsRef.current,
                error_message: typeof error === "string" ? error : String(error),
              });
            }
            setPlayingId(null);
            currentIdRef.current = null;
            currentTrackingPropsRef.current = null;
          }
        },
        onplayerror: (_soundId, error) => {
          if (isMountedRef.current) {
            if (currentTrackingPropsRef.current) {
              captureEvent(EventName.mw_audio_play_error, {
                ...currentTrackingPropsRef.current,
                error_message: typeof error === "string" ? error : String(error),
              });
            }
            setPlayingId(null);
            currentIdRef.current = null;
            currentTrackingPropsRef.current = null;
          }
        },
      });

      currentHowlRef.current = howl;
      currentIdRef.current = id;
      currentTrackingPropsRef.current = fullTrackingProps;

      howl.play();

      if (fullTrackingProps) {
        captureEvent(EventName.mw_audio_played, fullTrackingProps);
      }
      setPlayingId(id);
    },
    [destroyHowl, stop],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      destroyHowl();
    };
  }, [destroyHowl]);

  useEffect(() => {
    stop();
  }, [pathname, stop]);

  return <AudioContext.Provider value={{ playingId, play, stop }}>{children}</AudioContext.Provider>;
}

export function useAudio() {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within an AudioProvider");
  }
  return context;
}
